/**
 * DangerZonePanel — the shared red-bordered "Danger zone" card shell, extracted
 * from the venue + workshop danger zones (BON-202, two-tier reuse rule). Owns
 * only the chrome (red border + heading + the column of action rows); each
 * surface composes its own rows as children, so the persona/domain logic is NOT
 * folded in here.
 *
 * {@link DangerZoneRow} is the matching row primitive: a title + note on the
 * left and an arbitrary control on the right, with the dashed top divider that
 * separates stacked rows.
 */
import type { ReactNode } from 'react';

/** BonsaiBar danger red — shared by every danger-zone affordance. */
export const DANGER_RED = '#c0392b';

const titleStyle = { fontSize: 12.5, fontWeight: 700 } as const;
const noteStyle = { fontSize: 11, marginTop: 2, lineHeight: 1.45 } as const;

export interface DangerZonePanelProps {
  /** Heading text; defaults to "Danger zone". */
  title?: string;
  children: ReactNode;
}

export function DangerZonePanel({ title = 'Danger zone', children }: DangerZonePanelProps) {
  return (
    <section
      className="nb-card"
      style={{ borderColor: 'rgba(192,57,43,.35)', border: '1px solid rgba(192,57,43,.35)' }}
    >
      <div className="nb-card-hd" style={{ marginBottom: 10 }}>
        <h3 style={{ color: DANGER_RED }}>{title}</h3>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </section>
  );
}

export interface DangerZoneRowProps {
  title: string;
  /** Supporting copy under the title. */
  note: ReactNode;
  /** True → render the note in the warn color (e.g. a "blocked" explanation). */
  warn?: boolean;
  /** The right-hand control (button, input + button, …). */
  children: ReactNode;
  /** True → add the dashed top divider that separates a row from the one above. */
  divided?: boolean;
}

export function DangerZoneRow({ title, note, warn, children, divided }: DangerZoneRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        ...(divided ? { paddingTop: 12, borderTop: '1px dashed var(--nb-border)' } : {}),
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={titleStyle}>{title}</div>
        <div style={{ ...noteStyle, color: warn ? 'var(--nb-warn)' : 'var(--nb-muted)' }}>
          {note}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{children}</div>
    </div>
  );
}
